import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamWorkspaceService } from '../lib/teamWorkspaceService';
import { Workspace, CreateWorkspaceRequest } from '../types';

export const useUserWorkspaces = () => {
  return useQuery({
    queryKey: ['user-workspaces'],
    queryFn: () => teamWorkspaceService.getUserWorkspaces(),
    staleTime: 30000,
  });
};

export const useTeamWorkspaces = (teamId: string | null) => {
  return useQuery({
    queryKey: ['workspaces', teamId],
    queryFn: () => teamWorkspaceService.getTeamWorkspaces(teamId!),
    enabled: !!teamId,
    staleTime: 30000,
  });
};

export const useWorkspace = (workspaceId: string | null) => {
  return useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => teamWorkspaceService.getWorkspace(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 30000,
  });
};

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: CreateWorkspaceRequest) => teamWorkspaceService.createWorkspace(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['user-workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useCreateStandaloneWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ name, docketNumber, description }: { name: string; docketNumber: string; description?: string }) => 
      teamWorkspaceService.createStandaloneWorkspace(name, docketNumber, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
};

export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: teamWorkspaceService.deleteWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ workspaceId, updates }: { workspaceId: string; updates: { name?: string; description?: string } }) =>
      teamWorkspaceService.updateWorkspace(workspaceId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspace', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
};

